import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { API_BASE_URL } from '../services/api/config';
import { getJoinFormUrl, getVolunteerFormUrl } from '../utils/formUrls';

const Footer = () => {
  const { language } = useLanguage();
  const [userContents, setUserContents] = useState([]);
  const [mobileOpen, setMobileOpen] = useState({
    campaigns: false,
    policies: false,
    people: false,
    updates: false,
    press: false,
    about: false,
    shadow: false,
  });
  const toggleMobile = (key) => setMobileOpen(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    fetch(`${API_BASE_URL}/articles?limit=1000`)
      .then(res => res.json())
      .then(data => setUserContents(data.articles || []))
      .catch(() => { });
  }, []);

  const getUserContentArticle = (bn, en) => userContents.find(a => a.title === bn || a.titleEn === en);
  const getSlugByTitle = (bn, en) => {
    const article = getUserContentArticle(bn, en);
    return article ? article.slug : null;
  };

  // Common slugs with safe fallbacks
  const slugBangladesh2 = getSlugByTitle('বাংলাদেশ ২.০', 'Bangladesh 2.0') || 'bangladesh-2-0';
  const slugSecondRepublic = getSlugByTitle('সেকেন্ড রিপাবলিক', 'Second Republic') || 'second-republic';
  const slugManifesto = getSlugByTitle('ম্যানিফেস্টো', 'The Manifesto') || 'the-manifesto';
  const slugZeroUnemployment = getSlugByTitle(
    'শূন্য বেকারত্ব-ইয়ুথ এন্টারপ্রেনারশিপ-চতুর্থ শিল্প বিপ্লব',
    'Zero Unemployment: Youth entrepreneurship in the age of the Fourth Industrial Revolution'
  );
  const slugEconomicPowerhouse = getSlugByTitle(
    'অর্থনীতিতে এশিয়ার সুপার পাওয়ার-সমৃদ্ধ বাংলাদেশ',
    'An Economic Powerhouse: A prosperous Bangladesh in the heart of Asia'
  );
  const slugTechInnovation = getSlugByTitle(
    'তথ্যপ্রযুক্তিতে পৃথিবীর সেরা',
    'A Global Leader in Technology and Innovation'
  );

  const policyPaperUrl = '/Policy%20Paper%201%20-%20Youth%20Unemployment%20-%2023%20July.pdf';

  return (
    <footer className="bg-white text-gray-700 border-t border-gray-200">
      {/* Main Footer Content */}
      <div className="max-w-screen-xl mx-auto px-4 py-10">
        {/* Desktop/Large - 7-column layout with Contact on right */}
        <div className="hidden md:grid grid-cols-7 gap-6 items-start">

          {/* Left: AB Party identity (standalone) */}
          <div>
            <div className="flex items-center mb-4">
              <img
                src={require('../assets/ab_party_logo_300x300.jpg')}
                alt="AB Party Logo"
                className="w-12 h-12 mr-3 rounded-full border-2 border-gray-300 bg-white object-cover"
                loading="lazy"
              />
              <span className="font-bold text-xl text-gray-900">AB Party</span>
            </div>
            <div className="text-xs text-gray-500 leading-5">
              Saiham Sky View Tower (4th floor)
              <br />
              45 Bijoy Nagar Road
              <br />
              Dhaka 1000, Bangladesh
            </div>
            {/* Desktop map embed under address (like mobile view) */}
            <div className="mt-4 rounded-lg overflow-hidden border border-gray-200 hidden md:block">
              <iframe
                title="AB Party Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.5882940481445!2d90.40478927601825!3d23.7351018786901!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b854a34f9eaf%3A0xc01cd469f55569c8!2sSaiham%20Sky%20View%20Tower%2C%2045%20Bijoy%20Nagar%20Road%2C%20Dhaka%201000!5e0!3m2!1sen!2sbd!4v1710000000000!5m2!1sen!2sbd"
                width="100%"
                height="180"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Column 1: Campaigns + About Us */}
          <div>
            <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">{language === 'english' ? 'Campaigns' : 'ক্যাম্পেইন'}</h4>
            <ul className="space-y-2">
              <li><Link to="/volunteer-opportunities" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Learn about campaigning' : 'ক্যাম্পেইন সম্পর্কে জানুন'}</Link></li>
              <li><a href={getVolunteerFormUrl(language)} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Join as a volunteer' : 'স্বেচ্ছাসেবক হোন'}</a></li>
              <li><Link to="/candidates-map" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Find a campaign near you' : 'ক্যাম্পেইন খুঁজুন'}</Link></li>
              <li><Link to="/donate" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Fund our campaigns' : 'ক্যাম্পেইনের জন্য ফান্ড সংগ্রহ করুন'}</Link></li>
            </ul>

            <h4 className="font-bold text-gray-900 mb-4 mt-8 text-sm uppercase tracking-wide">{language === 'english' ? 'About Us' : 'আমাদের সম্পর্কে'}</h4>
            <ul className="space-y-2">
              <li><Link to={`/articles/${getSlugByTitle('Mission & Vision', 'Mission & Vision') || 'mission-vision'}`} className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Mission & Vision' : 'মিশন ও ভিশন'}</Link></li>
              <li><Link to={`/articles/${getSlugByTitle('Affiliated Organization', 'Affiliated Organization') || 'affiliated-organization'}`} className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Affiliated Organization' : 'সম্পৃক্ত সংস্থা'}</Link></li>
              <li><Link to="/ab-wings" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'AB Wings & Committees' : 'এবি উইংস ও কমিটি'}</Link></li>
              <li><Link to="/contact" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Contact' : 'যোগাযোগ'}</Link></li>
            </ul>
          </div>

          {/* Column 2: Policies & Ideology */}
          <div>
            <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">{language === 'english' ? 'Policies & Ideology' : 'নীতি ও আদর্শ'}</h4>
            <ul className="space-y-2">
              <li><Link to="/bangladesh-2-0" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Bangladesh 2.0' : 'বাংলাদেশ ২.০'}</Link></li>
              <li><Link to="/second-republic" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Second Republic' : 'সেকেন্ড রিপাবলিক'}</Link></li>
              <li><Link to={`/articles/${slugManifesto}`} className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Manifesto' : 'ম্যানিফেস্টো'}</Link></li>
            </ul>

            <h4 className="font-bold text-gray-900 mb-4 mt-8 text-sm uppercase tracking-wide">{language === 'english' ? 'Shadow Cabinet' : 'শ্যাডো কেবিনেট '}</h4>
            <ul className="space-y-2">
              <li><Link to="/articles/shadow-cabinet" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Shadow Cabinet' : 'শ্যাডো কেবিনেট '}</Link></li>
              <li><a href={policyPaperUrl} download className="text-secondary hover:text-red-700 transition-colors text-sm">{language === 'english' ? 'Download Policy Paper' : 'নীতিপত্র ডাউনলোড করুন'}</a></li>
              <li><Link to="/shop" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Shop' : 'শপ'}</Link></li>
            </ul>
          </div>

          {/* Column 3: AB People */}
          <div>
            <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">{language === 'english' ? 'AB People' : 'এবি পিপল'}</h4>
            <ul className="space-y-2">
              <li><Link to="/leadership" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'AB Leadership' : 'এবি নেতৃত্ব'}</Link></li>
              <li><Link to="/ab-wings" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'AB Wings' : 'এবি উইংস'}</Link></li>
            </ul>
          </div>

          {/* Column 4: Updates */}
          <div>
            <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">{language === 'english' ? 'Updates' : 'আপডেট'}</h4>
            <ul className="space-y-2">
              <li><Link to="/updates/latest" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Latest' : 'সর্বশেষ'}</Link></li>
              <li><Link to="/updates/featured" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Featured' : 'ফিচার্ড '}</Link></li>
              <li><Link to="/updates/notice-board" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Notice Board' : 'নোটিশ বোর্ড'}</Link></li>
            </ul>
          </div>

          {/* Column 5: Press Releases */}
          <div>
            <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">{language === 'english' ? 'Press Releases' : 'প্রেস রিলিজ'}</h4>
            <ul className="space-y-2">
              <li><Link to="/press-releases" className="text-gray-700 hover:text-primary transition-colors text-sm">{language === 'english' ? 'Press Releases' : 'প্রেস রিলিজ'}</Link></li>
            </ul>
          </div>

          {/* Column 6: Contact & Social (rightmost) */}
          <div>
            <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">
              {language === 'english' ? 'Contact & Social' : 'যোগাযোগ ও সামাজিক'}
            </h4>
            <div className="space-y-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-2">{language === 'english' ? 'Location' : 'অবস্থান'}</div>
                <div className="flex items-center gap-2">
                  <a
                    href="https://www.google.com/maps/place/Saiham+Sky+View+Tower,+45+Bijoy+Nagar+Road,+Dhaka+1000/@23.7351019,90.4073646,17z"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Map Location"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-primary text-white hover:bg-primary-dark transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M21 10c0 6-9 12-9 12S3 16 3 10a9 9 0 1 1 18 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </a>
                  <span className="text-sm text-gray-700">Saiham Sky View Tower</span>
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-1">Phone</div>
                <div className="text-sm text-gray-600">01888-012444</div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-1">Email</div>
                <div className="text-sm text-gray-600">info@abparty.org</div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-2">Follow Us</div>
                <div className="flex gap-3">
                  <a
                    href="https://x.com/ABPartyBD"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="X (Twitter)"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-black text-white hover:bg-gray-800 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M17.5 6.5L6.5 17.5" />
                      <path d="M6.5 6.5l11 11" />
                    </svg>
                  </a>
                  <a
                    href="https://www.facebook.com/ABPARTY20"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0" />
                    </svg>
                  </a>
                  <a
                    href="https://www.youtube.com/@abparty_official"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="YouTube"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Mobile - Accordion list mirroring More menu */}
        <div className="md:hidden">
          {/* AB Party identity section for mobile */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <img
                src={require('../assets/ab_party_logo_300x300.jpg')}
                alt="AB Party Logo"
                className="w-12 h-12 mr-3 rounded-full border-2 border-gray-300 bg-white object-cover"
                loading="lazy"
              />
              <span className="font-bold text-xl text-gray-900">AB Party</span>
            </div>
            <div className="text-sm text-gray-500 leading-5">
              Saiham Sky View Tower (4th floor)
              <br />
              45 Bijoy Nagar Road
              <br />
              Dhaka 1000, Bangladesh
            </div>
          </div>

          {[
            {
              key: 'campaigns', title: language === 'english' ? 'Campaigns' : 'ক্যাম্পেইন', items: [
                { to: '/volunteer-opportunities', label: language === 'english' ? 'Learn about campaigning' : 'ক্যাম্পেইন সম্পর্কে জানুন' },
                { href: getVolunteerFormUrl(language), label: language === 'english' ? 'Join as a volunteer' : 'স্বেচ্ছাসেবক হোন' },
                { to: '/candidates-map', label: language === 'english' ? 'Find a campaign near you' : 'ক্যাম্পেইন খুঁজুন' },
                { to: '/donate', label: language === 'english' ? 'Fund our campaigns' : 'ক্যাম্পেইনের জন্য ফান্ড সংগ্রহ করুন' },
              ]
            },
            {
              key: 'about', title: language === 'english' ? 'About Us' : 'আমাদের সম্পর্কে', items: [
                { to: `/articles/${getSlugByTitle('Mission & Vision', 'Mission & Vision') || 'mission-vision'}`, label: language === 'english' ? 'Mission & Vision' : 'মিশন ও ভিশন' },
                { to: `/articles/${getSlugByTitle('Affiliated Organization', 'Affiliated Organization') || 'affiliated-organization'}`, label: language === 'english' ? 'Affiliated Organization' : 'সম্পৃক্ত সংস্থা' },
                { to: '/ab-wings', label: language === 'english' ? 'AB Wings & Committees' : 'এবি উইংস ও কমিটি' },
                { to: '/contact', label: language === 'english' ? 'Contact' : 'যোগাযোগ' },
              ]
            },
            {
              key: 'policies', title: language === 'english' ? 'Policies & Ideology' : 'নীতি ও আদর্শ', items: [
                { to: '/bangladesh-2-0', label: language === 'english' ? 'Bangladesh 2.0' : 'বাংলাদেশ ২.০' },
                { to: '/second-republic', label: language === 'english' ? 'Second Republic' : 'সেকেন্ড রিপাবলিক' },
                { to: `/articles/${slugManifesto}`, label: language === 'english' ? 'Manifesto' : 'ম্যানিফেস্টো' },
              ]
            },
            {
              key: 'shadow', title: language === 'english' ? 'Shadow Cabinet' : 'শ্যাডো কেবিনেট ', items: [
                { to: '/articles/shadow-cabinet', label: language === 'english' ? 'Shadow Cabinet' : 'শ্যাডো কেবিনেট ' },
                { href: policyPaperUrl, label: language === 'english' ? 'Download Policy Paper' : 'নীতিপত্র ডাউনলোড করুন', download: true },
                { to: '/shop', label: language === 'english' ? 'Shop' : 'শপ' },
              ]
            },
            {
              key: 'people',
              title: language === 'english' ? 'AB People' : 'এবি পিপল',
              items: [
                { to: '/leadership', label: language === 'english' ? 'AB Leadership' : 'এবি নেতৃত্ব' },
                { to: '/ab-wings', label: language === 'english' ? 'AB Wings' : 'এবি উইংস' },
              ]
            },
            {
              key: 'updates', title: language === 'english' ? 'Updates' : 'আপডেট', items: [
                { to: '/updates/latest', label: language === 'english' ? 'Latest' : 'সর্বশেষ' },
                { to: '/updates/featured', label: language === 'english' ? 'Featured' : 'ফিচার্ড ' },
                { to: '/updates/notice-board', label: language === 'english' ? 'Notice Board' : 'নোটিশ বোর্ড' },
              ]
            },
            {
              key: 'press', title: language === 'english' ? 'Press Releases' : 'প্রেস রিলিজ', items: [
                { to: '/press-releases', label: language === 'english' ? 'Press Releases' : 'প্রেস রিলিজ' },
              ]
            },
          ].map(section => (
            <div key={section.key} className="border-t border-gray-200">
              <button type="button" className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold text-base" onClick={() => toggleMobile(section.key)}>
                <span>{section.title}</span>
                <svg className={`w-4 h-4 transition-transform ${mobileOpen[section.key] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-out ${mobileOpen[section.key] ? 'max-h-80' : 'max-h-0'}`}>
                <ul className="pb-2">
                  {section.items.map((item, idx) => (
                    <li key={idx}>
                      {item.to ? (
                        <Link to={item.to} className="block w-full text-left py-2 pl-2 text-gray-600 text-sm">{item.label}</Link>
                      ) : (
                        <a href={item.href} download={item.download} className="block w-full text-left py-2 pl-2 text-primary font-medium text-sm">{item.label}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}

          {/* Contact & Social (mobile) */}
          <div className="mt-6">
            <h4 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wide">{language === 'english' ? 'Contact & Social' : 'যোগাযোগ ও সামাজিক'}</h4>
            <div className="space-y-3">
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <iframe
                  title="AB Party Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.5882940481445!2d90.40478927601825!3d23.7351018786901!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b854a34f9eaf%3A0xc01cd469f55569c8!2sSaiham%20Sky%20View%20Tower%2C%2045%20Bijoy%20Nagar%20Road%2C%20Dhaka%201000!5e0!3m2!1sen!2sbd!4v1710000000000!5m2!1sen!2sbd"
                  width="100%"
                  height="180"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-1">Phone</div>
                <div className="text-sm text-gray-600">01888-012444</div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-1">Email</div>
                <div className="text-sm text-gray-600">info@abparty.org</div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-2">{language === 'english' ? 'Follow Us' : 'ফলো করুন'}</div>
                <div className="flex gap-3">
                  <a
                    href="https://x.com/ABPartyBD"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="X (Twitter)"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-black text-white hover:bg-gray-800 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M17.5 6.5L6.5 17.5" />
                      <path d="M6.5 6.5l11 11" />
                    </svg>
                  </a>
                  <a
                    href="https://www.facebook.com/ABPARTY20"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0" />
                    </svg>
                  </a>
                  <a
                    href="https://www.youtube.com/@abparty_official"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="YouTube"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Action Bar - Labour Style */}
      <div className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-screen-lg mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <div className="text-sm text-gray-500">
              &copy; 2025 AB Party. {language === 'english' ? 'All rights reserved.' : 'সর্বস্বত্ব সংরক্ষিত।'}
            </div>

            {/* Action Links - Labour Style */}
            <div className="flex gap-6">
              <a
                href={getVolunteerFormUrl(language)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-600 hover:text-red-700 transition-colors text-sm font-medium flex items-center gap-1"
              >
                {language === 'english' ? 'Volunteer' : 'স্বেচ্ছাসেবক'} →
              </a>
              <a
                href="/donate"
                className="text-red-600 hover:text-red-700 transition-colors text-sm font-medium flex items-center gap-1"
              >
                {language === 'english' ? 'Donate' : 'ডোনেট'} →
              </a>
              <a
                href={getJoinFormUrl(language)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-600 hover:text-red-700 transition-colors text-sm font-medium flex items-center gap-1"
              >
                {language === 'english' ? 'Join' : 'জয়েন'} →
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 